/**
 * Which declared content types the `quest-files` bucket accepts.
 *
 * The bucket enforces this list (20260929100000_quest_files_mime_allowlist);
 * this is its client-side mirror, so a file the bucket would refuse is flagged
 * in the tray before the user presses send instead of failing after an
 * upload. A test holds the two lists equal — change them together.
 *
 * The list is a second XSS layer, not the first: previews are still rebuilt
 * with a type chosen in lib/workspace/filePreview.ts, never the stored one.
 * What this layer adds is that nothing script-capable (SVG, XHTML, XML, HTML)
 * can be STORED, so a signed URL opened directly never runs script on the
 * storage origin.
 *
 * Pure.
 */

export const QUEST_FILES_ALLOWED_MIME_TYPES: readonly string[] = [
  // Images, listed one by one: `image/*` would admit image/svg+xml.
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/avif",
  "image/bmp",
  "image/tiff",
  // No audio or video type is script-capable, and camera and recorder
  // output varies by device (webm, mp4, quicktime, 3gpp, ogg...).
  "audio/*",
  "video/*",
  // Documents.
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/rtf",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  // Archives. Chrome on Windows reports .zip as x-zip-compressed.
  "application/zip",
  "application/x-zip-compressed",
  // What the browser declares for a file whose type it doesn't know
  // (File.type === ""): some Android pickers, unknown extensions.
  "application/octet-stream",
];

/**
 * The type the bucket will check for this file.
 *
 * supabase-js uploads a File as multipart and ignores `contentType`, so the
 * browser writes the part's Content-Type: `File.type`, or
 * application/octet-stream when that is empty. The storage server's multipart
 * parser then drops parameters and lowercases (`audio/webm;codecs=opus` is
 * checked and stored as `audio/webm`).
 */
export function declaredUploadType(fileType: string): string {
  const base = (fileType.split(";")[0] ?? "").trim().toLowerCase();
  return base || "application/octet-stream";
}

/** Same matching as storage-api's validateMimeType: exact, or `type/*`. */
export function isShareableType(fileType: string): boolean {
  const [type, subtype] = declaredUploadType(fileType).split("/");
  if (!type || !subtype) return false;
  return QUEST_FILES_ALLOWED_MIME_TYPES.some((allowed) => {
    const [allowedType, allowedSubtype] = allowed.split("/");
    return allowedType === type && (allowedSubtype === "*" || allowedSubtype === subtype);
  });
}

/**
 * True when a storage error is the bucket refusing the file's type.
 * storage-api answers HTTP 400 with body `statusCode: "415"`,
 * `error: "invalid_mime_type"`; supabase-js surfaces the body's statusCode.
 */
export function isUnsupportedTypeError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { statusCode?: unknown; error?: unknown; message?: unknown };
  if (String(e.statusCode) === "415") return true;
  if (e.error === "invalid_mime_type") return true;
  return typeof e.message === "string" && /mime type .* is not supported/i.test(e.message);
}
